/**
 * @fileoverview Archive (drop) and Restore dialog for a Sales Pipeline prospect.
 * @description Archiving takes a mandatory reason (POST /sales/prospects/{id}/archive; Super Admin
 * and Sales Head; refused while the prospect has an open target); restoring takes an optional note
 * (POST /sales/prospects/{id}/restore; Super Admin only). Both run against the prospect's current
 * version (ETag), loaded when the dialog opens; a change made by someone else since shows the
 * stale-version notice. Any other refusal shows as the dialog's error. Close is held while the
 * request is in flight, and the confirmation runs from the mutation itself (so it survives an
 * unmount). The parent mounts it only while it's open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useArchiveProspect } from '@/features/sales/hooks/useArchiveProspect'
import { useProspectDetails } from '@/features/sales/hooks/useProspectDetails'
import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { requestErrorMessage } from '@/features/sales/lib/request-error.utils'
import { isStaleVersionError } from '@/features/sales/lib/stale-version.utils'
import {
  ARCHIVE_NOTE_MAX_LENGTH,
  archiveProspectFormSchema,
  restoreProspectFormSchema,
  type ProspectArchiveFormValues,
} from '@/features/sales/schemas/prospect-archive-form.schema'
import Label from '@/shared/components/atoms/Label'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import SalesRequestError from './SalesRequestError'
import StaleVersionNotice from './StaleVersionNotice'

import type { ProspectArchiveResult, ProspectListItem } from '@/features/sales/types/prospect.types'
import type { FormEvent } from 'react'

/** @description Which change the dialog makes. */
export type ProspectArchiveMode = 'archive' | 'restore'

interface ProspectArchiveDialogProps {
  mode: ProspectArchiveMode
  prospect: ProspectListItem
  onClose: () => void
  onDone: (result: ProspectArchiveResult, prospect: ProspectListItem) => void
}

/**
 * @component
 * @param {ProspectArchiveDialogProps} props - The change, the prospect and the handlers.
 * @param {ProspectArchiveMode} props.mode - Archive or restore.
 * @param {ProspectListItem} props.prospect - The prospect to change.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 * @param {(result: ProspectArchiveResult, prospect: ProspectListItem) => void} props.onDone -
 * Called once the change is saved.
 */
function ProspectArchiveDialog({ mode, prospect, onClose, onDone }: ProspectArchiveDialogProps) {
  const { t } = useTranslation()
  const details = useProspectDetails(prospect.prospectId)
  const change = useArchiveProspect({
    /**
     * @description Reports the saved change to the page, even if this dialog has unmounted.
     * @param {ProspectArchiveResult} result - The prospect's archive status now.
     */
    onSuccess: (result) => onDone(result, prospect),
  })
  const close = useGuardedClose(onClose, change.isPending)
  const refreshSales = useRefreshSalesQueries()
  const isStale = change.isError && isStaleVersionError(change.error)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProspectArchiveFormValues>({
    resolver: zodResolver(
      mode === 'archive' ? archiveProspectFormSchema : restoreProspectFormSchema,
    ),
    defaultValues: { reasonNote: '' },
  })
  const loaded = details.data

  /**
   * @description Saves the change against the version shown; the mutation reports it.
   * @param {ProspectArchiveFormValues} values - The form's values.
   */
  const onValid = (values: ProspectArchiveFormValues) => {
    if (!loaded) return
    const base = { prospectId: prospect.prospectId, etag: loaded.etag }
    change.mutate(
      mode === 'archive'
        ? { ...base, action: 'archive', reasonNote: values.reasonNote }
        : { ...base, action: 'restore', reasonNote: values.reasonNote || null },
    )
  }

  /**
   * @description Clears a stale refusal and reloads the prospect's latest version.
   */
  const onRefresh = () => {
    change.reset()
    refreshSales()
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  const error = errors.reasonNote?.message
  const testPrefix = `${mode}-prospect`

  return (
    <Modal
      open
      onClose={close}
      data-testid={`${testPrefix}-dialog`}
      title={t(`sales.pipeline.${mode}.title`)}
      subtitle={prospect.bankName}
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4"
        data-testid={`${testPrefix}-form`}
      >
        <p className="text-[13.5px] text-ink" data-testid={`${testPrefix}-message`}>
          {t(`sales.pipeline.${mode}.message`, { bank: prospect.bankName })}
        </p>
        {mode === 'restore' && prospect.archiveReason && (
          <p className="text-[12.5px] text-slate" data-testid="restore-prospect-archived-reason">
            {t('sales.pipeline.restore.archivedReason', { reason: prospect.archiveReason })}
          </p>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="prospectReasonNote">{t(`sales.pipeline.${mode}.noteLabel`)}</Label>
          <textarea
            id="prospectReasonNote"
            data-testid={`${testPrefix}-note-input`}
            rows={3}
            maxLength={ARCHIVE_NOTE_MAX_LENGTH}
            aria-invalid={Boolean(error)}
            className="w-full rounded-lg border border-input-border bg-white px-3 py-2 text-[13.5px] text-ink"
            {...register('reasonNote')}
          />
          {error && (
            <span
              className="text-[11.5px] font-medium text-danger"
              data-testid={`${testPrefix}-note-error`}
            >
              {t(error)}
            </span>
          )}
        </div>
        {details.isError && (
          <p
            role="alert"
            className="text-[13px] text-danger"
            data-testid={`${testPrefix}-load-error`}
          >
            {requestErrorMessage(details.error, t)}
          </p>
        )}
        {isStale && <StaleVersionNotice onRefresh={onRefresh} isRefreshing={details.isFetching} />}
        {change.isError && !isStale && <SalesRequestError error={change.error} />}
        <DialogActions
          testIdPrefix={testPrefix}
          cancelLabel={t('sales.pipeline.form.cancel')}
          confirmLabel={t(`sales.pipeline.${mode}.submit`)}
          onCancel={close}
          confirmType="submit"
          isLoading={change.isPending}
          disabled={!details.isSuccess || details.isFetching || isStale}
        />
      </form>
    </Modal>
  )
}

export default ProspectArchiveDialog
